/**
 * Billing Page
 * Admin page for viewing billing reports and exporting data
 */

import React, { useMemo, useCallback } from 'react';
import { Card, Stack, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { BillingDashboard, MatterBillingData, StreamerBillingData } from '../components/echo/BillingDashboard';
import { useEchoBillingSummary } from '../api/echo';
import { generateBillingExcel, MatterSummaryRow } from '../utils/csvExport';

/**
 * Admin page for billing reports
 */
export function BillingPage() {
  const { theme } = useTheme();
  const { data: billingRows, loading: isLoading } = useEchoBillingSummary();

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const { matterData, streamerData, summary } = useMemo(() => {
    if (!billingRows) {
      return {
        matterData: [],
        streamerData: [],
        summary: {
          totalMatters: 0,
          totalStreamers: 0,
          totalVideos: 0,
          totalMinutes: 0,
          totalCost: 0,
        },
      };
    }

    const matterMap = new Map<string, MatterBillingData>();
    const streamerBillingMap = new Map<string, StreamerBillingData>();
    const streamerIds = new Set<number>();

    let totalMinutes = 0;
    let totalCost = 0;
    let totalVideos = 0;

    billingRows.forEach((row) => {
      const matterId = String(row.echo_matter_id);
      const rowCost = row.total_minutes * row.billing_rate;

      if (!matterMap.has(matterId)) {
        matterMap.set(matterId, {
          matterId,
          matterCode: row.mattercode,
          clientName: row.matter_name,
          billingRate: row.billing_rate,
          totalMinutes: 0,
          totalCost: 0,
          streamers: [],
        });
      }
      const matter = matterMap.get(matterId)!;

      matter.streamers.push({
        streamerName: row.streamer_name,
        videoCount: row.video_count,
        totalMinutes: row.total_minutes,
        totalCost: rowCost,
      });
      matter.totalMinutes += row.total_minutes;
      matter.totalCost += rowCost;

      const streamerBillingKey = `${row.echo_streamer_id}-${matterId}`;
      streamerBillingMap.set(streamerBillingKey, {
        streamerName: row.streamer_name,
        matterCode: row.mattercode,
        clientName: row.matter_name,
        videoCount: row.video_count,
        totalMinutes: row.total_minutes,
        totalCost: rowCost,
      });

      streamerIds.add(row.echo_streamer_id);
      totalMinutes += row.total_minutes;
      totalCost += rowCost;
      totalVideos += row.video_count;
    });

    const activeMatters = Array.from(matterMap.values()).sort((a, b) =>
      a.matterCode.localeCompare(b.matterCode)
    );

    const sortedStreamerData = Array.from(streamerBillingMap.values()).sort((a, b) => {
      const nameCompare = a.streamerName.localeCompare(b.streamerName);
      if (nameCompare !== 0) return nameCompare;
      return a.matterCode.localeCompare(b.matterCode);
    });

    return {
      matterData: activeMatters,
      streamerData: sortedStreamerData,
      summary: {
        totalMatters: activeMatters.length,
        totalStreamers: streamerIds.size,
        totalVideos,
        totalMinutes,
        totalCost,
      },
    };
  }, [billingRows]);

  const handleExportCSV = useCallback(() => {
    // Build aggregated matter summary rows for export
    const matterSummaryRows: MatterSummaryRow[] = matterData.map((matter) => ({
      matterCode: matter.matterCode,
      clientName: matter.clientName,
      billingRate: matter.billingRate,
      videoCount: matter.streamers.reduce((sum, s) => sum + s.videoCount, 0),
      totalMinutes: matter.totalMinutes,
      totalCost: matter.totalCost,
    }));
    generateBillingExcel(matterSummaryRows, streamerData);
  }, [matterData, streamerData]);

  return (
    <Stack gap="lg">
      {/* Page Header */}
      <div>
        <h1 style={{ margin: 0, ...textStyles.h3, color: textColor }}>
          Billing Manager
        </h1>
        <p style={{ margin: 0, marginTop: spacing.xs, ...textStyles.body, color: mutedColor }}>
          View billing statistics and export reports
        </p>
      </div>

      {/* Billing Dashboard */}
      <BillingDashboard
        matterData={matterData}
        streamerData={streamerData}
        summary={summary}
        isLoading={isLoading}
        onExportCSV={handleExportCSV}
      />

      {/* Help Section */}
      <Card variant="outlined">
        <Card.Body>
          <Stack gap="sm">
            <h3 style={{ margin: 0, ...textStyles.label, color: textColor }}>
              About Billing Reports
            </h3>
            <ul
              style={{
                margin: 0,
                paddingLeft: spacing.lg,
                color: mutedColor,
                fontSize: fontSize.sm,
              }}
            >
              <li>Billing is calculated based on matter billing rates and video duration</li>
              <li>Only completed workflows are included in billing calculations</li>
              <li>Click on a matter row to expand and see streamer breakdown</li>
              <li>Export to Excel for detailed analysis or importing to billing systems</li>
            </ul>
          </Stack>
        </Card.Body>
      </Card>
    </Stack>
  );
}

export default BillingPage;
